/**
 * Streak — consecutive learning days (audit F-03).
 *
 * A day counts as soon as the user answers at least one question. Day
 * boundaries are LOCAL time on purpose (audit F-13 lesson: UTC boundaries
 * would reset the streak at 01:00/02:00 local time).
 *
 * The stored `streak` value is only "alive" while the last counted day is
 * today or yesterday — `getEffectiveStreak` returns 0 otherwise, so
 * displays and the readiness score never show a long-dead streak.
 */
import type { UserProfile } from '../../shared/types';

type StreakFields = Pick<UserProfile, 'streak' | 'lastStreakDate'>;

export function localDateString(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function previousLocalDateString(now: Date): string {
  return localDateString(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
}

/**
 * Streak update for a qualifying learning activity, or null when today is
 * already counted (callers can skip the write entirely then).
 */
export function computeStreakUpdate(
  user: StreakFields,
  now: Date = new Date()
): { streak: number; lastStreakDate: string } | null {
  const today = localDateString(now);
  if (user.lastStreakDate === today) return null;
  const continued = user.lastStreakDate === previousLocalDateString(now);
  return {
    streak: continued ? (user.streak || 0) + 1 : 1,
    lastStreakDate: today,
  };
}

/** Streak for display/scoring: 0 unless last counted day is today or yesterday. */
export function getEffectiveStreak(user: StreakFields, now: Date = new Date()): number {
  if (!user.lastStreakDate) return 0;
  const alive =
    user.lastStreakDate === localDateString(now) ||
    user.lastStreakDate === previousLocalDateString(now);
  return alive ? user.streak || 0 : 0;
}
